import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasChunkError: boolean;
}

/**
 * ChunkLoadError 자동 복구 ErrorBoundary
 *
 * 배포 후 구 번들 청크가 CDN에서 사라졌을 때 발생하는
 * ChunkLoadError를 감지하여 자동으로 페이지를 새로고침한다.
 * 무한 새로고침 방지를 위해 sessionStorage에 플래그를 저장한다.
 */
class ChunkErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State | null {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk') ||
      error.message?.includes('dynamically imported module');

    if (isChunkError) {
      return { hasChunkError: true };
    }
    return null;
  }

  componentDidCatch(error: Error) {
    if (this.state.hasChunkError) {
      const reloadKey = 'chunk_error_reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      // 10초 이내 재시도 방지 (무한 루프 차단)
      if (lastReload && now - Number(lastReload) < 10000) {
        console.error('ChunkLoadError 반복 발생 — 새로고침 중단', error);
        return;
      }

      sessionStorage.setItem(reloadKey, String(now));
      window.location.reload();
    }
  }

  render() {
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
